import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Phase E9 — drives the "Generate preview clip" button alongside the
// wizard's HTML live-preview panel. Subscribes to the existing
// shorts_wizard_<config_id> Action Cable channel for status updates
// from RenderPreviewJob; swaps the live preview for an MP4 once a
// render completes.
//
// State machine:
//   idle      — button visible, live HTML preview shown
//   rendering — button replaced by indeterminate progress bar
//   done      — MP4 plays in place of HTML preview; "Revert" link
//               restores the HTML preview
//   errored   — error message + retry
export default class extends Controller {
  static targets = [
    "idleState", "renderingState", "doneState", "erroredState",
    "generateButton", "renderingLabel", "video", "errorMessage"
  ]
  static values = {
    postUrl: String,
    configId: String,
    cableChannel: String,
    initialStatus: { type: String, default: "idle" },
    initialOutputUrl: { type: String, default: "" }
  }

  connect() {
    this.subscription = consumer.subscriptions.create(
      { channel: "ShortsWizardChannel", config_id: this.configIdValue },
      {
        received: (data) => this.handleCableMessage(data)
      }
    )

    if (this.initialStatusValue === "completed" && this.initialOutputUrlValue) {
      this.showDone(this.initialOutputUrlValue)
    } else if (this.initialStatusValue === "processing" || this.initialStatusValue === "pending") {
      this.showRendering()
    } else {
      this.showIdle()
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  async generate(event) {
    event?.preventDefault()
    this.showRendering()

    try {
      const response = await fetch(this.postUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        }
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        this.showError(payload.error || `Request failed (${response.status})`)
        return
      }

      // Cache hit — server already has the MP4 ready. Skip rendering state.
      if (payload.cached && payload.output_url) {
        this.showDone(payload.output_url)
      }
      // Otherwise we wait for the cable broadcast.
    } catch (e) {
      this.showError(e.message || "Network error")
    }
  }

  regenerate(event) {
    // For "Regenerate" — server caches by config_hash, so changing
    // nothing and clicking returns the cache hit. The button is
    // mostly useful after the user has tweaked something.
    this.generate(event)
  }

  revertToLivePreview() {
    // The live HTML preview lives in a sibling DOM subtree and was
    // never hidden — we simply collapse the MP4 panel back to idle
    // so the HTML preview is visually dominant again.
    this.showIdle()
  }

  handleCableMessage(data) {
    if (!data || data.type !== "preview_clip_update") return

    switch (data.status) {
      case "processing":
      case "pending":
        this.showRendering()
        break
      case "completed":
        if (data.output_url) this.showDone(data.output_url)
        break
      case "failed":
        this.showError(data.error_message || "Render failed")
        break
    }
  }

  showIdle() {
    this.toggleStates({ idle: true })
  }

  showRendering() {
    this.toggleStates({ rendering: true })
  }

  showDone(url) {
    this.toggleStates({ done: true })
    if (this.hasVideoTarget) {
      this.videoTarget.src = url
      this.videoTarget.load?.()
      this.videoTarget.play?.().catch(() => { /* autoplay may be blocked on first paint */ })
    }
    document.dispatchEvent(new CustomEvent("render-preview-clip:done", {
      detail: { configId: this.configIdValue, outputUrl: url }
    }))
  }

  showError(message) {
    this.toggleStates({ errored: true })
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
  }

  toggleStates({ idle = false, rendering = false, done = false, errored = false }) {
    if (this.hasIdleStateTarget) this.idleStateTarget.hidden = !idle
    if (this.hasRenderingStateTarget) this.renderingStateTarget.hidden = !rendering
    if (this.hasDoneStateTarget) this.doneStateTarget.hidden = !done
    if (this.hasErroredStateTarget) this.erroredStateTarget.hidden = !errored
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
